import {firstValueFrom, Subscription} from 'rxjs';
import {FileWatcherService} from '../../electron/file-watcher/file-watcher.service';
import {SnackbarService} from '../../snackbar/snackbar.service';
import {LocalFileService} from '../local-file.service';
import {ImportService} from '../../import/import.service';
import {VaultQuery} from '../../../queries/vault.query';
import {IPC} from '../../../constants/shared.constants';
import {Vault} from '../../../api/models/vault';
import {ServerSelectionService} from '../../server-selection/server-selection.service';
import {UploadFile} from '../../../models/import/upload-file.model';
import {CheckoutEventService} from '../../electron/checkout/checkout-event.service';
import {environment} from '../../../../environments/environment';
import {ElectronService} from '../../electron/electron.service';
import {getLocalVaultNameFormatByVault} from '../../../util/local-aptera-vault-name-by-vault.util';
import {Document} from '../../../api/models/document';
import {effect, inject, Injectable, NgZone, OnDestroy, signal, untracked} from '@angular/core';
import {ClipboardService} from '../../clipboard/clipboard.service';
import {LoggingService} from '../../logging/logging.service';
import {ImportDialogResult} from '../../../models/import/import-dialog-result.model';
import {CheckedOutDocumentService} from '../../checked-out-document/checked-out-document.service';
import {NavigationService} from '../../navigation/navigation.service';
import {extractHostname} from '../../../util/extract-hostname.util';
import {UploadFileQueue} from '../../../models/import/upload-file-queue.model';
import {FileWatcherEvent} from '../../../models/file-watcher-event.model';
import {FileWatcherEventTypeEnum} from '../../../enums/file-watcher-event-type.enum';
import {StatusFile} from '../../../models/status-file/status-file.model';
import {ImportLocationEnum} from '../../../enums/import-location.enum';
import {FileUploadStatusEnum} from '../../../enums/file-upload-status.enum';

@Injectable()
export class ElectronWindowsLocalFileService implements LocalFileService, OnDestroy {
    private readonly fileWatcherService = inject(FileWatcherService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly checkedOutDocumentService = inject(CheckedOutDocumentService);
    private readonly checkoutEventService = inject(CheckoutEventService);
    private readonly importService = inject(ImportService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly clipboardService = inject(ClipboardService);
    private readonly loggingService = inject(LoggingService);
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly navigationService = inject(NavigationService);
    private readonly ngZone = inject(NgZone);
    private readonly inProgressDirectory = environment.inProgressDirectory;
    private localFileUploadQueue: Array<UploadFileQueue>;
    private readonly fileDeletionQuery: { [path: string]: number };
    private readonly pathSeparator = '\\';
    private readonly subscriptions = new Subscription();
    private readonly fileWatcherEventQueue = new Array<FileWatcherEvent>();
    private isFileWatcherQueueProcessing = false;


    public constructor() {
        this.localFileUploadQueue = [];
        this.fileDeletionQuery = {};

        if (ElectronService.isElectronApp()) {

            effect(() => {
                const hostname = this.serverSelectionService.getCurrentServerHostnameSignal()();
                this.debugLog('beforeServerChange hostname:', hostname);
                untracked(async () => {
                    await this.backupCheckedOutFiles(hostname);
                })
                    .then();
            });

            let isInitialLoginEffect = true;
            effect(() => {
                const isAfterLogin = this.navigationService.getAfterLoginNavigationSignal()();
                untracked(async () => {
                    this.debugLog('isAfterLogin:', isAfterLogin);
                    if (!isAfterLogin) {
                        return;
                    }
                    await this.vaultQuery.waitForHasDataLoaded();
                    await this.initAmagnoDirectorySync();
                    if (isInitialLoginEffect) {
                        isInitialLoginEffect = false;
                        this.initEventListeners();
                    }
                })
                    .then();
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async saveFile(content: ArrayBuffer, filePath: string, openFile: boolean = false): Promise<void> {
        // This opens a dialog where the user can decide which folder the document is to be saved in
        await ElectronService.ipcRender?.invoke(IPC.SHARE.FILE, {
                filename: filePath,
                content
            })
            .then(createdFile => {
                const result = createdFile as { filename: string, error: unknown, filePath: string | null, successful: boolean };
                if (result.successful) {
                    this.snackbarService.showSuccess('FILE_DOWNLOAD_SUCCESS_MSG', { filepath: result.filePath });
                } else if (result.filePath) {
                    this.snackbarService.showError('FILE_DOWNLOAD_ERROR_MSG');
                } else {
                    this.snackbarService.showError('FILE_DOWNLOAD_NO_FOLDER_SELECTED_ERROR_MSG');
                }

                return createdFile;
            });
    }

    public async shareFile(content: ArrayBuffer, filename: string): Promise<void> {
        return this.saveFile(content, filename);
    }

    public async shareUrl(url: string): Promise<void> {
        await this.clipboardService.copyToClipboard(url);
    }

    public createVaultDirectory(vault: Vault): Promise<void> {
        const vaultDirectoryPath = getLocalVaultNameFormatByVault(vault) + this.pathSeparator + this.inProgressDirectory;
        ElectronService.ipcRender?.invoke(IPC.DIRECTORY.CREATE_MULTI, [vaultDirectoryPath]);
        return Promise.resolve();
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private debugLog(...args: Array<unknown>): void {
        if (!environment.production) {
            console.log('[ElectronWindowsLocalFileService]', ...args);
        }
    }

    private async initAmagnoDirectorySync(): Promise<void> {
        this.debugLog('initAmagnoDirectorySync');
        await this.syncVaultDirectories(); // Sync Vault Dir's in Amagno Dir - remove old ones when not needed anymore
        await this.restoreCheckedOutBackup(); // Get Backuped ckeckedout files from APPDATA/Local Amagno Folder and add them to the Vault Dir's in Amagno Dir
        await this.backupCheckedOutFiles(); // Create a new Backup of checkedout files in Vault Dir's in Amagno Dir to save at APPDATA/Local Amagno Folder
    }

    private initEventListeners(): void {
        this.debugLog('initEventListeners');
        this.startWatchingFiles();
        this.subscriptions.add(this.checkoutEventService.deleteVaultDirectoryEvent.subscribe(async path => {
            const isDeleted = await this.deleteDirectory(path);
            this.checkoutEventService.deleteVaultDirectoryResultEvent.emit(isDeleted);
        }));
        this.subscriptions.add(this.checkoutEventService.deleteFsFileEntryEvent.subscribe(async path => {
            const isDeleted = await this.deleteFileByPath(path);
            this.checkoutEventService.deleteFsFileEntryResultEvent.emit(isDeleted);
        }));
        this.subscriptions.add(this.checkoutEventService.syncLocalDocumentEvent.subscribe(async (documentId) => {
            try {
                const document = await this.checkedOutDocumentService.fetchAndGetCheckedOutDocumentById(documentId);
                await this.syncFileToDevice(document);
                this.checkoutEventService.syncLocalDocumentResultEvent.emit(documentId);
            } catch {
                console.error('no document found, could not open:', documentId);
            }
        }));
        this.subscriptions.add(this.checkoutEventService.openLocalDocumentEvent.subscribe(async (documentId) => {
            try {
                const document = await this.checkedOutDocumentService.fetchAndGetCheckedOutDocumentById(documentId);
                const vault = this.vaultQuery.getVaultById(document.vaultId);
                if (!vault) {
                    console.error('vault not found', document.vaultId);
                    return;
                }

                await this.openLocalFileByPath(`${getLocalVaultNameFormatByVault(vault)}/${this.inProgressDirectory}/${document.name}`);
            } catch (error) {
                console.error(`document with id ${documentId} not found:`, error);
            }
        }));
    }

    private async syncFileToDevice(document: Document): Promise<boolean> {
        const vault = this.vaultQuery.getVaultById(document.vaultId);
        if (!vault) {
            console.error('vault not found', document.vaultId);
            return false;
        }

        const vaultName = getLocalVaultNameFormatByVault(vault);
        if (ElectronService.ipcRender) {
            return ElectronService.ipcRender.invoke<{ path: string; exists: boolean; openFile: boolean }, string | boolean>(
                    IPC.FILE.CHECK,
                    `${vaultName}/${this.inProgressDirectory}/${document.originalFilename}`,
                    false
                )
                .then(async (args) => {
                    if (!args.exists) {
                        const buffer = await this.getCheckedOutFileContentArrayBuffer(document.id);
                        await this.saveFileToAmagnoDocumentPath(buffer, `${vaultName}/${this.inProgressDirectory}/${document.name}`);
                    }
                    return true;
                });
        }
        return false;
    }

    private startWatchingFiles(): void {
        this.initHandleFileEvent();
        this.fileWatcherService.watch();
    }

    private async saveFileToAmagnoDocumentPath(content: ArrayBuffer, filePath: string): Promise<void> {
        await ElectronService.ipcRender?.invoke(IPC.FILE.CREATE, {
                filePath,
                content
            })
            .then(createdFile => {
                this.snackbarService.showSuccess('FILE_DOWNLOAD_SUCCESS_SHORT_MSG');

                return createdFile;
            });
    }

    private async syncVaultDirectories(): Promise<void> {
        const vaults = this.vaultQuery.getAll();
        this.debugLog('vaults loaded', vaults);
        const expectedDirNames = vaults.map(vault => getLocalVaultNameFormatByVault(vault));
        const result = await ElectronService.ipcRender?.invoke<{ needToAdd: Array<string> }, unknown>(
            IPC.DIRECTORY.SYNC, null, '', expectedDirNames
        );
        if (result?.needToAdd?.length) {
            const missingPaths = result.needToAdd.map(dirName => `${dirName}/${this.inProgressDirectory}`);
            await ElectronService.ipcRender?.invoke(IPC.DIRECTORY.CREATE_MULTI, missingPaths);
        }
    }

    private async backupCheckedOutFiles(serverHostname?: string): Promise<void> {
        const hostname = serverHostname ?? extractHostname(this.serverSelectionService.getSelectedServer()?.displayUrl);
        if (!hostname) {
            this.debugLog('backupCheckedOutFiles — skipped, cant get server hostname');
            return;
        }
        this.debugLog('backupCheckedOutFiles — hostname:', hostname);
        await ElectronService.ipcRender?.invoke(IPC.DIRECTORY.BACKUP_DESKTOP, { serverHostname: hostname })
            .catch(err => {
                console.error('BACKUP_DESKTOP invoke failed:', err);
            });
    }

    private async restoreCheckedOutBackup(): Promise<void> {
        const serverHostname = extractHostname(this.serverSelectionService.getSelectedServer()?.displayUrl);
        if (!serverHostname) {
            this.debugLog('restoreCheckedOutBackup — skipped, cant get server hostname');
            return;
        }

        this.debugLog('restoreCheckedOutBackup — hostname:', serverHostname);
        const vaults = this.vaultQuery.getAll();
        const vaultDirNames = vaults.map(vault => getLocalVaultNameFormatByVault(vault));
        await ElectronService.ipcRender?.invoke(IPC.DIRECTORY.RESTORE_DESKTOP_BACKUP,
                {
                    serverHostname,
                    vaultDirNames
                })
            .catch(err => {
                console.error('RESTORE_DESKTOP_BACKUP invoke failed:', err);
            });
    }

    private async deleteDirectory(path: string): Promise<boolean> {
        return ElectronService.ipcRender ? ElectronService.ipcRender?.invoke(IPC.DIRECTORY.REMOVE, path)
            .then(args => {
                return !!args;
            }) : false;
    }

    private async deleteFileByPath(path: string): Promise<boolean> {
        const fileName = path.split(this.pathSeparator)
            .pop();

        if (!fileName) {
            this.snackbarService.showError('FILE_DELETE_ERROR_MSG', new Error(`Can't get file name from path: ${path}`));
            return false;
        }

        return ElectronService.ipcRender ? ElectronService.ipcRender?.invoke(IPC.FILE.REMOVE, path)
            .then(args => {
                return !!args;
            }) : false;
    }

    private async moveToNotImported(path: string): Promise<boolean> {
        const pathArr = path.split(this.pathSeparator);
        const lastElem = pathArr.pop();
        if (pathArr.at(-1)!.toLowerCase() === 'desktop') {
            pathArr.pop();
        }
        pathArr.push('_NotImported', lastElem as string);
        const newPath = [...new Set(pathArr)].join(this.pathSeparator);
        if (!ElectronService.ipcRender) {
            return false;
        }
        const fileMoveResult = await ElectronService.ipcRender.invoke<{ result: boolean; path: string }, unknown>(IPC.FILE.MOVE, {
            oldPath: path,
            newPath
        });

        if (fileMoveResult.result) {
            return true;
        }
        this.snackbarService.showError('ERROR.FILE_MOVED');
        return false;
    }


    private async openLocalFileByPath(filePath: string): Promise<boolean> {
        const fileOpened = await ElectronService.ipcRender?.invoke<Record<string, unknown>, string>(IPC.FILE.OPEN, filePath)
            .then((data: Record<string, unknown>): Promise<boolean> => {
                if (data.error) {
                    console.error(`Error opening file at path: ${this.loggingService.maskPath(filePath)}:`, data.error);
                }

                return new Promise(() => data.openFile);
            });
        return !!fileOpened;
    }

    private handleImportDialogForFilesInDirectory(): void {
        // Just return if there are no files left in the upload queue.
        if (this.localFileUploadQueue.length === 0) {
            this.debugLog('No files to handle');
            return;
        }

        // Filter all files with the same vault and import target as the first pending file upload.
        // Prioritise non-InProgress targets (Documents, magnets) so that normal-folder files are
        // always offered for import before desktop/checked-out files.
        const firstEntry = this.localFileUploadQueue.find(file => file.target.id !== 'InProgress')
            ?? this.localFileUploadQueue[0];
        const firstUploadFileVault = firstEntry.vault;
        const firstUploadFileTarget = firstEntry.target;
        const firstUploadFilesQueue = this.localFileUploadQueue.filter(file => {
            return file.vault.id === firstUploadFileVault.id && file.target.id === firstUploadFileTarget.id;
        });
        const firstUploadFiles: Array<UploadFile> = firstUploadFilesQueue.map(uploadFileQueue => uploadFileQueue.file);

        const importDialog = this.importService.showImportDialog(firstUploadFiles, firstUploadFileVault.id, undefined, firstUploadFileTarget, undefined, undefined, true, true);
        this.subscriptions.add(importDialog?.afterClosed((importDialogResult?: ImportDialogResult) => {
            this.handleClosedImportDialog(importDialogResult, firstUploadFilesQueue)
                .catch(error => console.error('Error handling the closed import dialog:', error));
        }));
    }

    private async handleClosedImportDialog(importDialogResult: ImportDialogResult | undefined, firstUploadFilesQueue: Array<UploadFileQueue>): Promise<void> {
        if (importDialogResult) {
            await this.handleImportDialogResult(importDialogResult);
        } else {
            // User dismissed the dialog — move files to _NotImported so they don't prompt again
            for (const entry of firstUploadFilesQueue) {
                await this.moveToNotImported(entry.iFsEvent.filePath);
            }
            this.localFileUploadQueue = this.localFileUploadQueue.filter(entry => !firstUploadFilesQueue.includes(entry));
        }

        this.fileWatcherService.emitImportDoneEvent();

        // Make a recursive call to handle pending imports to other vaults or other targets inside the same vault if there are any.
        setTimeout(() => {
                this.handleImportDialogForFilesInDirectory();
            }, 1
        );
    }

    private async handleImportDialogResult(importDialogResult: ImportDialogResult): Promise<void> {
        const statusFiles = importDialogResult.statusFiles;
        for (const statusFile of statusFiles) {
            if (!statusFile.upload()) {
                console.error('Upload undefined in result');
                continue;
            }

            const uploadResult = statusFile.upload();
            if (!uploadResult?.filePath) {
                console.error('missing filePath in uploadResult');
                continue;
            }
            const filePath = uploadResult.filePath;

            const queueIndex = this.localFileUploadQueue.findIndex(file => file.iFsEvent.filePath === filePath);
            if (queueIndex !== -1) {
                this.localFileUploadQueue.splice(queueIndex, 1);
            }

            switch (uploadResult.status) {
                case FileUploadStatusEnum.Done:
                    // A file already in the desktop folder imported to InProgress becomes the checked-out document — keep it for editing.
                    // Files from outside the desktop folder must always be deleted after import (the checked-out copy lands in desktop).
                    if (importDialogResult.importLocation !== 'InProgress' ||
                        !filePath.includes(`${this.pathSeparator}${this.inProgressDirectory}${this.pathSeparator}`)) {
                        this.deleteFileByPath(filePath)
                            .then();
                    }
                    break;
                case FileUploadStatusEnum.Error:
                    await this.moveToNotImported(filePath);
                    break;
                default:
                    console.error('undefined status for file upload');
                    break;
            }
        }
    }

    private addFileToUploadQueue(newUploadFileQueue: UploadFileQueue): void {
        // check if file is in queue before adding it
        if (this.localFileUploadQueue.some(uploadFileQueue => uploadFileQueue.iFsEvent.filePath === newUploadFileQueue.iFsEvent.filePath)) {
            this.debugLog('File already added', newUploadFileQueue);
            return;
        }
        this.localFileUploadQueue.push(newUploadFileQueue);
        this.debugLog('File added', this.localFileUploadQueue);
    }

    private async getFileFromPath(filePath: string): Promise<UploadFile | undefined> {
        return ElectronService.ipcRender?.invoke<Record<string, unknown> | undefined, string>(IPC.FILE.GET, filePath)
            .then(uploadFile => {
                if (uploadFile) {
                    const file: File = new File([uploadFile.buffer as BlobPart], uploadFile.name as string);
                    (file as UploadFile).filePath = filePath;
                    (file as UploadFile).creationDate = uploadFile.creationDate as number | undefined;
                    return file;
                } else {
                    return undefined;
                }
            });
    }

    private async processQueuedEvents(): Promise<void> {
        this.debugLog('handleQueuedEvents', this.fileWatcherEventQueue.length);
        if (this.fileWatcherEventQueue.length === 0) {
            this.debugLog('Queue is empty. Stop queue processing!');
            this.isFileWatcherQueueProcessing = false;
            return;
        }

        this.isFileWatcherQueueProcessing = true;
        try {
            await this.handleQueuedEvents();
        } catch (err) {
            console.error('processQueuedEvents: unhandled error in handleQueuedEvents, continuing queue:', err);
        }

        if (this.fileWatcherEventQueue.length > 0) {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.processQueuedEvents();
                });
            }, 1);
            return;
        }
        this.isFileWatcherQueueProcessing = false;
    }

    private async handleQueuedEvents(): Promise<void> {
        const fileWatcherEvent = this.fileWatcherEventQueue.shift();
        if (!fileWatcherEvent) {
            return;
        }
        this.debugLog('Handle event:', fileWatcherEvent.type);
        if (fileWatcherEvent.type === FileWatcherEventTypeEnum.AddDone) {
            this.handleImportDialogForFilesInDirectory();
            return;
        }
        const file = await this.getFileFromPath(fileWatcherEvent.filePath);
        if (!file) {
            this.debugLog('could not read file, skipping:', fileWatcherEvent.filePath);
            return;
        }
        const filename = fileWatcherEvent.filePath.split(this.pathSeparator)
            .pop() as string;
        if (filename.startsWith('~')) {
            this.debugLog('skipping temp file:', filename);
            return;
        }
        const idPart = fileWatcherEvent.filePath.substring(fileWatcherEvent.filePath.indexOf('--') + 2, fileWatcherEvent.filePath.indexOf('--') + 10);

        const vaults = this.vaultQuery.getAll();
        const vault = vaults
            .find(vault => vault.id.includes(idPart));
        if (!vault) {
            this.debugLog('no matching vault found for idPart:', idPart, '— path:', this.loggingService.maskPath(fileWatcherEvent.filePath));
            return;
        }
        this.debugLog('matched vault:', vault.id, 'for file:', filename);

        switch (fileWatcherEvent.type) {
            case FileWatcherEventTypeEnum.Add:
                await this.handleFileAddEvent(fileWatcherEvent, file, vault, filename);
                break;
            case FileWatcherEventTypeEnum.Change:
                await this.handleFileChangeEvent(fileWatcherEvent, file, vault, filename);
                break;
            default:
                break;
        }
    }

    private async handleFileAddEvent(fileWatcherEvent: FileWatcherEvent, file: UploadFile, vault: Vault, filename: string): Promise<void> {
        if (fileWatcherEvent.filePath.includes('desktop')) {
            const document = await this.checkedOutDocumentService.getCheckedOutDocumentByVaultIdAndName(vault.id, filename);
            if (!document) {
                this.addFileToUploadQueue({
                    file,
                    vault,
                    iFsEvent: fileWatcherEvent,
                    target: {
                        id: ImportLocationEnum.InProgress,
                        name: 'InProgress'
                    }
                });
            }
        } else {
            this.addFileToUploadQueue({
                file,
                vault,
                iFsEvent: fileWatcherEvent,
                target: {
                    id: ImportLocationEnum.Documents,
                    name: 'Documents'
                }
            });
        }
    }

    private async handleFileChangeEvent(fileWatcherEvent: FileWatcherEvent, file: UploadFile, vault: Vault, filename: string): Promise<void> {
        const document = await this.checkedOutDocumentService.getCheckedOutDocumentByVaultIdAndName(vault.id, filename);
        if (this.fileDeletionQuery[fileWatcherEvent.filePath]) {
            window.clearTimeout(this.fileDeletionQuery[fileWatcherEvent.filePath]);
            delete this.fileDeletionQuery[fileWatcherEvent.filePath];
        }
        if (document) {
            // upload file update
            const statusFile: StatusFile = {
                file,
                iconId: document.iconId,
                upload: signal(undefined)
            };
            await this.importService.uploadExistingCheckedOutDocument(document, statusFile);
        } else {
            console.error('no checked out document found!');
        }
    }

    private initHandleFileEvent(): void {
        this.subscriptions.add(this.fileWatcherService.subscribableFileWatcherEvents$.subscribe(async (fileWatcherEvent: FileWatcherEvent) => {
            this.debugLog('file event received:', fileWatcherEvent.type, fileWatcherEvent.filePath);
            this.fileWatcherEventQueue.push(fileWatcherEvent);
            if (!this.isFileWatcherQueueProcessing) {
                await this.processQueuedEvents();
            }
        }));
    }

    private async getCheckedOutFileContentArrayBuffer(documentId: string): Promise<ArrayBuffer> {
        const bufferPromise = firstValueFrom(this.checkoutEventService.fetchAndGetCheckedOutFileContentResultEvent);
        this.checkoutEventService.fetchAndGetCheckedOutFileContentEvent.emit(documentId);
        return bufferPromise;
    }
}
